// components/Logo.jsx
// Usage:
//   <Logo />                        — auto dark/light from body.dark
//   <Logo size="xs" />              — xs | sm | md (default) | lg
//   <Logo showTagline={false} />    — hide tagline

import { useState, useEffect } from "react";

const SIZES_WITH_TAGLINE = {
  xs: { width: 150, height: 42 },   // mobile compact with tagline
  sm: { width: 190, height: 53 },   // desktop navbar / compact
  md: { width: 280, height: 78 },   // footer / cards / downtime
  lg: { width: 380, height: 106 },  // hero / splash
};

const SIZES_NO_TAGLINE = {
  xs: { width: 145, height: 33 },   // mobile navbar (sharp, prominent, full height)
  sm: { width: 175, height: 40 },   // desktop navbar / compact
  md: { width: 260, height: 59 },   // admin / cards
  lg: { width: 360, height: 81 },   // hero / splash
};

function useBordsTheme() {
  const [dark, setDark] = useState(
    () => typeof document !== "undefined" && document.body.classList.contains("dark")
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.body.classList.contains("dark"))
    );
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

// Detect mobile inside the component so navbar can auto-switch properly
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [breakpoint]);
  return mobile;
}

export default function Logo({ size = "md", showTagline = true, mobileSize, forceDark, className = "", style = {}, onClick }) {
  const themeIsDark = useBordsTheme();
  const isDark      = forceDark !== undefined ? forceDark : themeIsDark;
  const isMobile    = useIsMobile();

  // If caller passed mobileSize, use it on mobile; else scale nicely on mobile
  const resolvedSize = isMobile
    ? (mobileSize || (size === "sm" ? "xs" : size === "md" ? "sm" : size === "lg" ? "md" : size))
    : size;

  const sizeMap = showTagline ? SIZES_WITH_TAGLINE : SIZES_NO_TAGLINE;
  const { width, height } = sizeMap[resolvedSize] || sizeMap.md;

  const c = isDark
    ? { wordmark:"#F5F3EE", bMark:"#F5F3EE", accent:"#4A5D3A", accentFaint:"rgba(74,93,58,0.3)", tagline:"#EDE6D6" }
    : { wordmark:"#2B2A28", bMark:"#2B2A28", accent:"#4A5D3A", accentFaint:"rgba(74,93,58,0.22)", tagline:"#4A5D3A" };

  // When tagline is hidden, use tight 400x90 viewBox so letters fill the height without bottom dead space
  const viewBox = showTagline ? "0 0 400 112" : "0 0 400 90";

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    } else if (typeof window !== "undefined") {
      if (typeof window.__bordsNavigate === "function") {
        window.__bordsNavigate("home");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.location.href = "/";
      }
    }
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="BORDS — Bored of Routine Designs · Consciously Cut"
      role="img"
      className={className}
      onClick={handleClick}
      style={{ display:"block", cursor:"pointer", ...style }}
    >
      {/* ── B mark — Anchor Mark ── */}
      <g>
        <rect x="0"  y="0"  width="11" height="72" rx="2" fill={c.bMark} />
        <rect x="11" y="0"  width="40" height="11" rx="2" fill={c.bMark} />
        <rect x="11" y="30" width="33" height="11" rx="2" fill={c.bMark} />
        <rect x="11" y="61" width="40" height="11" rx="2" fill={c.bMark} />
        <rect x="42" y="0"  width="11" height="41" rx="2" fill={c.bMark} />
        <rect x="39" y="30" width="11" height="42" rx="2" fill={c.bMark} />
        {/* anchor underline */}
        <rect x="0"  y="76" width="53" height="3"  rx="1.5" fill={c.accent} />
      </g>

      {/* ── ORDS wordmark ── */}
      <text
        x="66"
        y="76"
        fontFamily="'Cormorant Garamond', serif"
        fontSize="74"
        fontWeight="600"
        letterSpacing="8"
        fill={c.wordmark}
      >
        ORDS
      </text>

      {/* ── Baseline rule ── */}
      <rect x="0" y="88" width="400" height="1.5" rx="1" fill={c.accentFaint} />

      {/* ── Tagline: Bored of Routine Designs · Consciously Cut ── */}
      {showTagline && (
        <text
          x="200"
          y="108"
          fontFamily="'DM Sans', sans-serif"
          fontSize="8.8"
          fontWeight="600"
          letterSpacing="2.8"
          textAnchor="middle"
          fill={c.tagline}
        >
          BORED OF ROUTINE DESIGNS · CONSCIOUSLY CUT
        </text>
      )}
    </svg>
  );
}
